import React, { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { motion, AnimatePresence } from "framer-motion";

/**
 * RefPricingList.jsx  +  RefPricingEditor.jsx
 * (réunis ici pour faciliter le collage ; tu peux scinder en deux fichiers plus tard)
 *
 * - RefPricingList : vue tableau des règles existantes (recherche, tri, pagination, delete, ouvrir Editor)
 * - RefPricingEditor : édition complète (modal) + upsert
 *
 * Pré-requis : table `public.ref_pricing` (voir SQL fourni précédemment)
 */

// -----------------------------
// Editor (modal)
// -----------------------------
export function RefPricingEditor({ open, rule, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    id: rule?.id || null,
    artist: rule?.artist || "",
    movement: rule?.movement || "",
    medium: rule?.medium || "",
    size_min_cm2: rule?.size_min_cm2 ?? "",
    size_max_cm2: rule?.size_max_cm2 ?? "",
    price_per_m2: rule?.price_per_m2 ?? "",
    ci_low_pct: rule?.ci_low_pct ?? 10,
    ci_high_pct: rule?.ci_high_pct ?? 10,
    recency_months: rule?.recency_months ?? 24,
    source_note: rule?.source_note || "",
    confidence: rule?.confidence ?? 60,
  }));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Re-sync quand 'rule' change (une seule dépendance)
  useEffect(() => {
    setForm({
      id: rule?.id ?? null,
      artist: rule?.artist ?? "",
      movement: rule?.movement ?? "",
      medium: rule?.medium ?? "",
      size_min_cm2: rule?.size_min_cm2 ?? "",
      size_max_cm2: rule?.size_max_cm2 ?? "",
      price_per_m2: rule?.price_per_m2 ?? "",
      ci_low_pct: rule?.ci_low_pct ?? 10,
      ci_high_pct: rule?.ci_high_pct ?? 10,
      recency_months: rule?.recency_months ?? 24,
      source_note: rule?.source_note ?? "",
      confidence: rule?.confidence ?? 60,
    });
  }, [rule]);

  async function onSubmit(e) {
    e.preventDefault(); setSaving(true); setMsg("");
    try {
      const payload = {
        artist: form.artist || null,
        movement: form.movement || null,
        medium: form.medium || null,
        size_min_cm2: form.size_min_cm2 ? parseInt(form.size_min_cm2, 10) : null,
        size_max_cm2: form.size_max_cm2 ? parseInt(form.size_max_cm2, 10) : null,
        price_per_m2: form.price_per_m2 !== "" ? Number(form.price_per_m2) : null,
        ci_low_pct: form.ci_low_pct !== "" ? parseInt(form.ci_low_pct, 10) : 10,
        ci_high_pct: form.ci_high_pct !== "" ? parseInt(form.ci_high_pct, 10) : 10,
        recency_months: form.recency_months !== "" ? parseInt(form.recency_months, 10) : 24,
        source_note: form.source_note || null,
        confidence: form.confidence !== "" ? parseInt(form.confidence, 10) : 60,
      };
      let res;
      if (form.id) {
        res = await supabase.from('ref_pricing').update(payload).eq('id', form.id).select();
      } else {
        res = await supabase.from('ref_pricing').insert([payload]).select();
      }
      if (res.error) throw res.error;
      setMsg('✅ Enregistré');
      onSaved?.(res.data?.[0] || null);
      onClose?.();
    } catch (err) {
      setMsg('❌ ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <motion.div initial={{opacity:0, y:10}} animate={{opacity:1,y:0}} exit={{opacity:0,y:10}}
        className="w-full max-w-3xl bg-white rounded-2xl shadow-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-semibold">{form.id? 'Éditer règle' : 'Nouvelle règle'}</h3>
          <button className="text-sm text-gray-600" onClick={onClose}>Fermer</button>
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs mb-1">Artiste (optionnel)</label>
            <input className="form-control-artsy" value={form.artist} onChange={e=>setForm(f=>({...f, artist:e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs mb-1">Mouvement</label>
            <input className="form-control-artsy" value={form.movement} onChange={e=>setForm(f=>({...f, movement:e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs mb-1">Médium</label>
            <input className="form-control-artsy" value={form.medium} onChange={e=>setForm(f=>({...f, medium:e.target.value}))} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs mb-1">cm² min</label>
              <input type="number" className="form-control-artsy" value={form.size_min_cm2}
                onChange={e=>setForm(f=>({...f, size_min_cm2:e.target.value}))} />
            </div>
            <div>
              <label className="block text-xs mb-1">cm² max</label>
              <input type="number" className="form-control-artsy" value={form.size_max_cm2}
                onChange={e=>setForm(f=>({...f, size_max_cm2:e.target.value}))} />
            </div>
          </div>
          <div>
            <label className="block text-xs mb-1">€/m²</label>
            <input type="number" className="form-control-artsy" value={form.price_per_m2}
              onChange={e=>setForm(f=>({...f, price_per_m2:e.target.value}))} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs mb-1">- % (basse)</label>
              <input type="number" className="form-control-artsy" value={form.ci_low_pct}
                onChange={e=>setForm(f=>({...f, ci_low_pct:e.target.value}))} />
            </div>
            <div>
              <label className="block text-xs mb-1">+ % (haute)</label>
              <input type="number" className="form-control-artsy" value={form.ci_high_pct}
                onChange={e=>setForm(f=>({...f, ci_high_pct:e.target.value}))} />
            </div>
            <div>
              <label className="block text-xs mb-1">Fraîcheur (mois)</label>
              <input type="number" className="form-control-artsy" value={form.recency_months}
                onChange={e=>setForm(f=>({...f, recency_months:e.target.value}))} />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs mb-1">Source / Note</label>
            <textarea className="form-control-artsy h-16 w-full" value={form.source_note}
              onChange={e=>setForm(f=>({...f, source_note:e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs mb-1">Confiance (0–100)</label>
            <input type="number" className="form-control-artsy" value={form.confidence}
              onChange={e=>setForm(f=>({...f, confidence:e.target.value}))} />
          </div>
          <div className="md:col-span-2 flex items-center gap-2 mt-2">
            <button type="submit" className="btn-artsy" disabled={saving}>{saving? 'Enregistrement…' : 'Enregistrer'}</button>
            {msg && <div className="text-sm">{msg}</div>}
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// -----------------------------
// List (avec recherche/tri/pagination)
// -----------------------------
export default function RefPricingList() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ by: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ref_pricing")
      .select("*")
      .order(sort.by, { ascending: sort.dir === "asc" });
    if (!error) setRules(data || []);
    setLoading(false);
  }, [sort.by, sort.dir]);

  useEffect(() => { fetchRules(); }, [fetchRules]);


  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let arr = [...rules];
    if (needle) {
      arr = arr.filter(r =>
        (r.artist||"").toLowerCase().includes(needle) ||
        (r.movement||"").toLowerCase().includes(needle) ||
        (r.medium||"").toLowerCase().includes(needle) ||
        (r.source_note||"").toLowerCase().includes(needle)
      );
    }
    return arr;
  }, [rules, q]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  function onSort(by) {
    setSort(s => ({ by, dir: s.by===by ? (s.dir==='asc'?'desc':'asc') : 'asc' }));
  }

  async function onDelete(id) {
    if (!window.confirm('Supprimer cette règle ?')) return;
    const { error } = await supabase.from('ref_pricing').delete().eq('id', id);
    if (!error) setRules(r => r.filter(x => x.id !== id));
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-semibold">Références de prix (ref_pricing)</h2>
        <button className="btn-artsy" onClick={()=>{ setEditing(null); setEditorOpen(true); }}>+ Nouvelle règle</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <input className="form-control-artsy md:col-span-2" placeholder="Recherche (mouvement, medium, artiste, source)"
          value={q} onChange={e=>{setQ(e.target.value); setPage(1);}} />
        <select className="form-control-artsy" value={`${sort.by}:${sort.dir}`} onChange={e=>{const [by,dir]=e.target.value.split(':'); setSort({by,dir});}}>
          <option value="created_at:desc">Plus récentes</option>
          <option value="created_at:asc">Plus anciennes</option>
          <option value="price_per_m2:desc">€/m² ↓</option>
          <option value="price_per_m2:asc">€/m² ↑</option>
          <option value="confidence:desc">Confiance ↓</option>
          <option value="confidence:asc">Confiance ↑</option>
        </select>
        <button className="btn-secondary-artsy" onClick={fetchRules}>↻ Rafraîchir</button>
      </div>

      <div className="overflow-auto border rounded-2xl bg-white shadow">
        <table className="w-full text-sm border-separate border-spacing-y-1">
          <thead>
            <tr className="text-left text-gray-600">
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('movement')}>Mouvement</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('artist')}>Artiste</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('medium')}>Médium</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('size_min_cm2')}>cm² min</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('size_max_cm2')}>cm² max</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('price_per_m2')}>€/m²</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('ci_low_pct')}>- %</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('ci_high_pct')}>+ %</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('recency_months')}>Fraîcheur</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('confidence')}>Confiance</th>
              <th className="px-3 py-2 cursor-pointer" onClick={()=>onSort('created_at')}>Créée</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={12} className="px-3 py-6 text-center text-gray-500">Chargement…</td></tr>
            )}
            {!loading && paged.map(r => (
              <tr key={r.id} className="bg-gray-50">
                <td className="px-3 py-2">{r.movement || '—'}</td>
                <td className="px-3 py-2">{r.artist || '—'}</td>
                <td className="px-3 py-2">{r.medium || '—'}</td>
                <td className="px-3 py-2">{r.size_min_cm2 ?? '—'}</td>
                <td className="px-3 py-2">{r.size_max_cm2 ?? '—'}</td>
                <td className="px-3 py-2">{r.price_per_m2?.toLocaleString('fr-FR') || '—'}</td>
                <td className="px-3 py-2">{r.ci_low_pct ?? '—'}</td>
                <td className="px-3 py-2">{r.ci_high_pct ?? '—'}</td>
                <td className="px-3 py-2">{r.recency_months ?? '—'}</td>
                <td className="px-3 py-2">{r.confidence ?? '—'}</td>
                <td className="px-3 py-2">{r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-2">
                    <button className="btn-secondary-artsy" onClick={()=>{ setEditing(r); setEditorOpen(true); }}>Éditer</button>
                    <button className="btn-secondary-artsy" onClick={()=>onDelete(r.id)}>Supprimer</button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && paged.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-6 text-center text-gray-500">Aucune règle</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center justify-between">
        <div className="text-sm text-gray-600">Page {page}</div>
        <div className="flex gap-2">
          <button className="btn-secondary-artsy" onClick={()=>setPage(p=>Math.max(1,p-1))}>Précédent</button>
          <button className="btn-secondary-artsy" onClick={()=>setPage(p=>p+1)}>Suivant</button>
        </div>
      </div>

      <AnimatePresence>
        {editorOpen && (
          <RefPricingEditor
            open={editorOpen}
            rule={editing}
            onClose={()=>setEditorOpen(false)}
            onSaved={(row)=>{ setEditorOpen(false); fetchRules(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
